import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import { actions } from '../../listView.reducer'
import { ListViewDispatchContext } from '../../listView.context'
import { ExpertFieldOptions } from '../../../Expert/components/ExpertBulkCreate/helpers/expertFieldOptions'
import { v4 as uuidv4 } from 'uuid'
import {
  AddToProcessingActionEnum,
  AddToProcessingExpertTypeEnum,
  InitializeProcessingActionEnum,
  sendAddToProcessingMetric,
  sendInitializeProcessingMetric
} from '../../../Expert/components/ExpertBulkCreate/helpers/expertBulkCreate.metrics'
import { massProcessingApi } from '@atheneum/api'

const AddToProjectHandler = inject(
  'modalStore',
  'expertBulkCreateStore',
  'expertSearchStore'
)(
  observer(
    class InternalAddToProjectHandler extends Component {
      state = { progress: 'initializing...' }
      constructor(props) {
        super(props)

        this.addToProjectFormOpened = false
        this.bulkExpertCreateIdentifier = `ADD_TO_PROJECT_${uuidv4()}`
        this.bulkCreateTrackingIds = []
        this.bulkCreateCompletedExperts = []
        this.bulkCreateFailedExperts = []

        this.processingExpertsDispose = props.expertBulkCreateStore.observeProcessingExperts(
          experts => {
            const expertsToProcess = experts.filter(
              e => e.customIdentifier === this.bulkExpertCreateIdentifier
            )
            if (expertsToProcess.length > 0) {
              this.bulkCreateTrackingIds.push(
                ...expertsToProcess.map(e => ({
                  bulkCreateTrackingId: e.bulkCreateTrackingId,
                  listItemId: e.data.listItemId,
                  listId: e.data.listId
                }))
              )
            }
          }
        )

        this.completedExpertsDispose = props.expertBulkCreateStore.observeCompletedExperts(
          experts => {
            const expertsToProcess = experts.filter(
              e => e.customIdentifier === this.bulkExpertCreateIdentifier
            )
            if (expertsToProcess.length > 0) {
              this.bulkCreateCompletedExperts.push(...expertsToProcess)
              this.tryOpenExpertToProjectForm()
            }
          }
        )

        this.errorExpertsDispose = props.expertBulkCreateStore.observeErrorExperts(
          experts => {
            const expertsToProcess = experts.filter(
              e => e.customIdentifier === this.bulkExpertCreateIdentifier
            )
            if (expertsToProcess.length > 0) {
              this.bulkCreateFailedExperts.push(...expertsToProcess)
              this.tryOpenExpertToProjectForm()
            }
          }
        )

        this.cancelledExpertsDispose = props.expertBulkCreateStore.observeCancelledExperts(
          experts => {
            const expertsToProcess = experts.filter(
              e => e.customIdentifier === this.bulkExpertCreateIdentifier
            )
            if (expertsToProcess.length > 0) {
              this.bulkCreateFailedExperts.push(...expertsToProcess)
              this.tryOpenExpertToProjectForm()
            }
          }
        )

        this.props.expertBulkCreateStore.processExpertsProgress.observe(e => {
          if (e.name !== this.bulkExpertCreateIdentifier) return
          this.setState({ progress: e.newValue + '%' })
        })
        this.processExperts()
      }

      componentWillUnmount() {
        this.processingExpertsDispose()
        this.completedExpertsDispose()
        this.errorExpertsDispose()
        this.cancelledExpertsDispose()
      }

      processExperts = async () => {
        const mappedExperts = this.props.items.map(i => {
          if (i.data.expertSync?.expertId) {
            return {
              ...i.data,
              id: i.data.expertSync.expertId,
              listItemId: i.id,
              listId: i.listId
            }
          } else {
            const expert = JSON.parse(JSON.stringify(i.data))

            // Special email mapping for AL
            expert.email = ''

            if (!expert.address) {
              expert.address = {}
            }

            if (!expert.address.emails) {
              expert.address.emails = []
            }

            if (Array.isArray(i.formatted?.emails)) {
              const allEmails = []
              for (let email of i.formatted?.emails) {
                if (typeof email === 'string' || email instanceof String) {
                  allEmails.push(email)
                } else if (typeof email === 'object' && email.email) {
                  allEmails.push(email.email)
                } else {
                  // None of the above
                }
              }

              expert.address.emails = allEmails.map(e => ({ email: e.trim() }))
            } else if (
              typeof initialState.formatted?.emails === 'string' ||
              i.formatted?.emails instanceof String
            ) {
              const splitEmails = i.formatted.emails
                .split(',')
                .map(e => e.trim())
                .filter(e => e)
              expert.address.emails = splitEmails.map(e => ({ email: e }))
            }

            // ADD PHONE MAPPING
            if (!expert.address.phones) {
              expert.address.phones = []
            }

            if (Array.isArray(expert.formatted?.phones)) {
              const allPhones = []
              for (let phone of expert.formatted?.phones) {
                if (typeof phone === 'string' || phone instanceof String) {
                  allPhones.push(phone)
                } else if (typeof phone === 'object' && phone.phoneNum) {
                  //allPhones.push(phone.phoneNum)
                } else {
                  // None of the above
                }
              }

              expert.address.phones = allPhones.map(e => ({
                phoneNum: e.trim()
              }))
            } else if (
              typeof expert.formatted?.phones === 'string' ||
              expert.formatted?.phones instanceof String
            ) {
              const splitPhones = expert.formatted.phones
                .split(',')
                .map(e => e.trim())
                .filter(e => e)
              expert.address.phones = splitPhones.map(e => ({ phoneNum: e }))
            }

            expert.formatted = undefined

            return { ...expert, listItemId: i.id, listId: i.listId }
          }
        })

        if (mappedExperts.length) {
          for (let expert of mappedExperts) {
            expert.bulkCreateCustomIdentifier = this.bulkExpertCreateIdentifier

            let expertTypeMetricDimension = null
            if (expert.id) {
              if (Number(expert.id)) {
                expertTypeMetricDimension =
                  AddToProcessingExpertTypeEnum.EXISTING_EXPERT
              } else {
                expertTypeMetricDimension = AddToProcessingExpertTypeEnum.PDL
              }
            } else {
              if (expert.pdlId) {
                expertTypeMetricDimension = AddToProcessingExpertTypeEnum.PDL
              } else {
                expertTypeMetricDimension =
                  AddToProcessingExpertTypeEnum.NON_EXISTING_EXPERT
              }
            }

            sendAddToProcessingMetric(
              AddToProcessingActionEnum.AL_ADD_TO_PROJECT,
              expertTypeMetricDimension
            )
          }

          sendInitializeProcessingMetric(
            InitializeProcessingActionEnum.AL_ADD_TO_PROJECT
          )

          await this.props.expertBulkCreateStore.addExpertsToProcessingQueue(
            mappedExperts,
            ExpertFieldOptions.EXPERT_ADD_TO_PROJECT_OPTIONS,
            null,
            true,
            false,
            true,
            this.bulkExpertCreateIdentifier
          )
        } else {
          this.props.listViewDispatchContext({ type: actions.END_ACTION })
        }
      }

      tryOpenExpertToProjectForm = async () => {
        if (
          !this.addToProjectFormOpened &&
          this.bulkCreateTrackingIds.every(
            e =>
              this.bulkCreateCompletedExperts.some(
                k => k.bulkCreateTrackingId === e.bulkCreateTrackingId
              ) ||
              this.bulkCreateFailedExperts.some(
                k => k.bulkCreateTrackingId === e.bulkCreateTrackingId
              )
          )
        ) {
          if (this.bulkCreateCompletedExperts.length > 0) {
            this.props.modalStore.show({
              type: 'EXPERT_TO_PROJECT_FORM',
              props: {
                experts: toJS(this.bulkCreateCompletedExperts.map(e => e.data))
              }
            })

            for (let expert of this.bulkCreateCompletedExperts) {
              const foundItemInfo = this.bulkCreateTrackingIds.find(
                e => e.bulkCreateTrackingId === expert.bulkCreateTrackingId
              )
              const foundListItemId = foundItemInfo.listItemId
              const foundListId = foundItemInfo.listId

              const item = await massProcessingApi.getItem(foundListItemId)
              if (!item.data?.expertSync?.expertId) {
                // Item doesn't have expertSync Id, it means it was either a PDL or manually added expert. We created an expert in this case, remove AL item and replace with new one
                const preppedExpert = await this.props.expertSearchStore.prepExpertForPinning(
                  expert.data
                )

                const createdItem = await massProcessingApi.createItem(
                  foundListId,
                  preppedExpert
                )
                this.props.listViewDispatchContext({
                  type: actions.ADD_ITEM_DONE,
                  payload: createdItem
                })

                await massProcessingApi.deleteItem(foundListItemId)
                this.props.listViewDispatchContext({
                  type: actions.REMOVE_ITEM_DONE,
                  payload: item.id
                })
              } else {
                // Once an item is created, the created expert is used instead of this AL Item
                await massProcessingApi.updateField({
                  id: foundListItemId,
                  key: 'expertSync',
                  value: { finished: true, expertId: expert.data.id }
                })

                this.props.listViewDispatchContext({
                  type: actions.UPDATE_FIELD,
                  payload: {
                    id: foundListItemId,
                    field: { name: 'expertSync' },
                    value: { finished: true, expertId: expert.data.id }
                  }
                })
              }
            }
          }

          if (this.bulkCreateFailedExperts.length > 0) {
            for (let expert of this.bulkCreateFailedExperts) {
              const foundListItemId = this.bulkCreateTrackingIds.find(
                e => e.bulkCreateTrackingId === expert.bulkCreateTrackingId
              ).listItemId

              this.props.listViewDispatchContext({
                type: actions.UPDATE_FIELD,
                payload: {
                  id: foundListItemId,
                  field: { name: 'checked' },
                  value: false
                }
              })
            }
          }

          this.props.listViewDispatchContext({ type: actions.END_ACTION })
        }
      }

      render() {
        return (
          <div style={{ marginLeft: '10px' }}>
            Adding to project {this.state.progress}
          </div>
        )
      }
    }
  )
)

const CreateExpertsPageWrapper = props => (
  <ListViewDispatchContext.Consumer>
    {context => (
      <AddToProjectHandler listViewDispatchContext={context} {...props} />
    )}
  </ListViewDispatchContext.Consumer>
)

export const actionStages = [
  {
    validators: [],
    component: CreateExpertsPageWrapper,
    full: false
  }
]

export const addToProject = {
  name: 'addToProject',
  title: 'Add to project',
  component: ActionComponent,
  stages: actionStages
}

export const initialState = {}

function ActionComponent({ massAction, items }) {
  const [state, setState] = React.useState(initialState)

  return addToProject.stages[massAction.stage].component({
    items,
    state,
    setState
  })
}
